import { useMemo, useContext, createContext, useEffect } from 'react'
import { useDispatch, useSelector, useSelectorWithRef } from '../../../redux/hooks'
import templateActions from '../../../redux/modules/template/actions'
import templateSelectors from '../../../redux/modules/template/selectors'
import authSelectors from '../../../redux/modules/auth/selectors'
import { useUnsubscribe } from '../../../hooks/shared/useUnsubscribe'
import { Template } from '@leadgen/models/client'
import { useRegisteredComponents } from '../../../hooks/shared/useRegisteredComponents'

const ActiveTemplateContext = createContext<{
  registerComponent: () => () => void
  activeTemplate: Template | null
  activeTemplateImageUrl: string | null
  loading: boolean
  error: string | undefined
} | null>(null)

const useActiveTemplate = (
  { skipRegistration }: { skipRegistration: boolean } = { skipRegistration: false },
) => {
  const context = useContext(ActiveTemplateContext)
  if (!context) {
    throw new Error('no ActiveTemplateProvider found when calling useActiveTemplate()')
  }
  const { registerComponent, activeTemplate, loading, error, activeTemplateImageUrl } = context
  useEffect(() => {
    if (!skipRegistration) {
      return registerComponent()
    }
  }, [registerComponent, skipRegistration])
  return {
    activeTemplate,
    activeTemplateImageUrl,
    loading,
    error,
  }
}

const ActiveTemplateProvider = ({ children }) => {
  const { unsubscribe, registerUnsubscribe } = useUnsubscribe()
  const dispatch = useDispatch()
  const { registerComponent, hasRegisteredComponents, registeredComponentsRef } =
    useRegisteredComponents()
  const activeTemplate = useSelector(templateSelectors.getTemplate)
  const activeTemplateImageUrl = useSelector(templateSelectors.getTemplateImageUrl)
  const [loading, loadingRef] = useSelectorWithRef(templateSelectors.getLoading())
  const [, initialRef] = useSelectorWithRef(templateSelectors.getInitial)
  const error = useSelector(templateSelectors.getError)
  const companyId = useSelector(authSelectors.getCompanyId)
  const role = useSelector(authSelectors.getRole)
  const emailVerified = useSelector(authSelectors.getEmailVerified)
  useEffect(() => {
    if (
      companyId &&
      emailVerified &&
      role !== 'super-admin' &&
      hasRegisteredComponents &&
      (!loadingRef.current || initialRef.current)
    ) {
      dispatch(templateActions.loadTemplate({ companyId }))
        .unwrap()
        .then(({ unsubscribe: unsubscribeFromTemplate }) => {
          if (!registeredComponentsRef.current.length) {
            unsubscribeFromTemplate()
            unsubscribe()
          } else {
            unsubscribe()
            registerUnsubscribe(unsubscribeFromTemplate)
          }
        })

      return
    }
  }, [
    companyId,
    emailVerified,
    dispatch,
    hasRegisteredComponents,
    loadingRef,
    initialRef,
    registerUnsubscribe,
    registeredComponentsRef,
    unsubscribe,
    role,
  ])
  useEffect(() => {
    if (!hasRegisteredComponents) {
      unsubscribe()
      return
    }
  }, [hasRegisteredComponents, unsubscribe])
  useEffect(() => {
    return () => {
      unsubscribe()
    }
  }, [unsubscribe])
  const value = useMemo(
    () => ({
      registerComponent,
      activeTemplate: activeTemplate || null,
      activeTemplateImageUrl,
      loading,
      error,
    }),
    [activeTemplate, activeTemplateImageUrl, error, loading, registerComponent],
  )
  return <ActiveTemplateContext.Provider value={value}>{children}</ActiveTemplateContext.Provider>
}

export { useActiveTemplate, ActiveTemplateProvider }
export default ActiveTemplateProvider
